import type { BuildPlan, BuildRequestResult } from '@/api/schemas';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { WarningsList } from '@/components/WarningsList';
import { EnvPill, PlatformIcon, Tag } from '@/components/badges';
import { ProviderBadge } from '@/components/ProviderBadge';
import { humanize } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useWording } from '@/lib/useAudience';
import { PLATFORM_LABEL } from './buildHelpers';
import { executionModeLabel } from './buildLabels';
import { planCheckText, planCommitOf, type CopyContext } from './runBuildCopy';
import { checkTone } from './runBuildModel';

/** The dry-run result: one card per platform plus the request's warnings. */
export function PlanView({ result, ctx = {} }: { result: BuildRequestResult; ctx?: CopyContext }) {
  return (
    <div data-testid="run-build-plan" className="flex flex-col gap-2">
      <Kicker className="text-subtle">Preview · nothing started yet</Kicker>
      {result.plan.map((p) => (
        <PlanCard key={`${p.platform}-${p.profile.id}`} plan={p} ctx={ctx} />
      ))}
      <WarningsList warnings={result.warnings} compact />
    </div>
  );
}

function PlanCard({ plan: p, ctx }: { plan: BuildPlan; ctx: CopyContext }) {
  return (
    <div data-testid={`run-build-plan-${p.platform}`} data-blocked={p.blocked} className={cn('flex flex-col gap-2 rounded-lg border bg-card p-3 text-[13px]', p.blocked && 'border-failed/40')}>
      <div className="flex items-center gap-2">
        <PlatformIcon platform={p.platform} />
        <span className="font-medium text-foreground">{PLATFORM_LABEL[p.platform]} build</span>
        <ProviderBadge kind={p.providerAccount.kind} className="ml-auto" />
        <Tag className="font-sans text-muted-foreground">{executionModeLabel(p.executionMode)}</Tag>
      </div>
      <PlanFacts plan={p} />
      {p.approvalRequired ? <PlanApproval reason={p.approvalReason} /> : null}
      {p.envMissing.length > 0 ? <PlanMissing missing={p.envMissing} /> : null}
      {p.checks.length > 0 ? <PlanChecks plan={p} ctx={ctx} /> : null}
      {p.blocked ? <PlanBlocked /> : null}
    </div>
  );
}

/** The plan's facts as a definition list: profile, env, version, env hash, and (when known) cost and queue position. */
function PlanFacts({ plan: p }: { plan: BuildPlan }) {
  const mapped = p.providerConfig && typeof p.providerConfig.profile === 'string' ? p.providerConfig.profile : undefined;
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 font-mono text-xs text-muted-foreground">
      <dt className="text-subtle">profile</dt>
      <dd className="truncate">
        {p.profile.slug}
        {mapped ? <span className="text-subtle"> → {mapped}</span> : null}
      </dd>
      <dt className="text-subtle">env</dt>
      <dd>
        <EnvPill env={p.environment} />
      </dd>
      <dt className="text-subtle">version</dt>
      <dd className="tabular">
        {p.versionName ?? '—'} · {p.platform === 'ios' ? 'build' : 'versionCode'} {p.buildNumber ?? '—'}
      </dd>
      <dt className="text-subtle">env hash</dt>
      <dd className="truncate" title={p.envContentHash}>
        {p.envContentHash ?? '—'}
      </dd>
      <OptionalFacts plan={p} />
    </dl>
  );
}

function OptionalFacts({ plan: p }: { plan: BuildPlan }) {
  return (
    <>
      {p.estimatedCostUsd !== null && p.estimatedCostUsd !== undefined ? (
        <>
          <dt className="text-subtle">cost</dt>
          <dd>~USD {p.estimatedCostUsd.toFixed(2)}</dd>
        </>
      ) : null}
      {p.queue?.position ? (
        <>
          <dt className="text-subtle">queue</dt>
          <dd>position {p.queue.position}</dd>
        </>
      ) : null}
    </>
  );
}

function PlanApproval({ reason }: { reason: string | null | undefined }) {
  return (
    <p className="flex items-start gap-1.5 rounded-md border border-awaiting/30 bg-awaiting/10 px-2 py-1 text-xs text-awaiting">
      <Icon name="approval" size={14} className="mt-px" />
      {reason ?? 'Approval required'}
    </p>
  );
}

function PlanMissing({ missing }: { missing: BuildPlan['envMissing'] }) {
  return (
    <div className="rounded-md border border-failed/30 bg-failed/10 px-2 py-1.5 text-xs text-failed">
      <span className="font-medium">Missing requirements</span>
      <ul className="mt-1 flex flex-wrap gap-1">
        {missing.map((m) => (
          <li key={m.key} className="rounded-sm border border-failed/30 px-1.5 font-mono text-[11px]" title={m.description ?? undefined}>
            {m.key}
            {m.reason !== 'missing' ? ` · ${humanize(m.reason)}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

const CHECK_TONE = {
  passed: { icon: 'check_circle', text: 'text-succeeded' },
  failed: { icon: 'error', text: 'text-failed' },
  other: { icon: 'warning', text: 'text-awaiting' },
} as const;

function PlanChecks({ plan: p, ctx }: { plan: BuildPlan; ctx: CopyContext }) {
  const say = useWording();
  const copy: CopyContext = { ...ctx, commitSha: planCommitOf(p.checks, ctx.commitSha) };
  return (
    <ul className="flex flex-col gap-1">
      {p.checks.map((c) => {
        const tone = CHECK_TONE[checkTone(c.status)];
        return (
          <li key={c.key} data-check={c.key} className="flex items-start gap-1.5 text-xs">
            <Icon name={tone.icon} size={14} className={cn('mt-px shrink-0', tone.text)} />
            <span className="min-w-0 text-muted-foreground">{say(planCheckText(c, p.platform, copy))}</span>
          </li>
        );
      })}
    </ul>
  );
}

function PlanBlocked() {
  return (
    <p className="flex items-center gap-1.5 text-xs font-medium text-failed">
      <Icon name="block" size={14} />
      Blocked — a real request would be rejected
    </p>
  );
}
